import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { usePaywall } from "@/contexts/PaywallContext";
import { toast } from "sonner";

const PlayerContext = createContext(null);

// ----- YouTube IFrame loader (singleton) -----
let _ytReady = null;
const loadYouTubeAPI = () => {
  if (_ytReady) return _ytReady;
  _ytReady = new Promise((resolve) => {
    if (window.YT && window.YT.Player) {
      resolve(window.YT);
      return;
    }
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    tag.async = true;
    document.head.appendChild(tag);
    window.onYouTubeIframeAPIReady = () => resolve(window.YT);
  });
  return _ytReady;
};

const isFullTrack = (t) => !!(t && (t._source === "audius" || t._source === "upload" || t._fullTrack));

export const PlayerProvider = ({ children }) => {
  const { user, premium } = useAuth();
  const { showPaywall } = usePaywall();
  const audioRef = useRef(null);              // HTML5 audio for Audius / direct MP3
  const ytPlayerRef = useRef(null);           // YouTube IFrame player instance
  const ytPollRef = useRef(null);             // setInterval id for time polling
  const ytReadyRef = useRef(false);           // YT API & player ready
  const engineRef = useRef("audio");          // 'audio' | 'youtube'
  // Live mirror of the user's premium status so async playback callbacks
  // always read the latest value (closure-safe). Paid tiers must never
  // hear the 30s iTunes preview — when YouTube fails for them we skip
  // forward to the next playable track instead.
  const isPremiumRef = useRef(false);
  useEffect(() => {
    isPremiumRef.current = !!premium?.active;
  }, [premium]);
  const [queue, setQueue] = useState([]);
  const [index, setIndex] = useState(-1);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0); // ms
  const [duration, setDuration] = useState(0); // ms
  const [volume, setVolume] = useState(0.8);
  const [shuffle, setShuffle] = useState(false);
  const [repeat, setRepeat] = useState("off");
  const [likedIds, setLikedIds] = useState(new Set());

  // ----- Audio commercials (free-tier only) -----
  // Configured once on mount via /api/ads/config; refreshed on premium changes.
  const adsConfigRef = useRef({ ads_enabled: false, frequency: 3, tier: "free" });
  const playCountRef = useRef(0);              // tracks played this session
  const adImpressionRef = useRef(null);        // { id, campaign, started_at }
  const [currentAd, setCurrentAd] = useState(null);  // null | { ad }

  useEffect(() => {
    let cancelled = false;
    const load = () =>
      api.get("/ads/config").then((r) => {
        if (cancelled) return;
        adsConfigRef.current = {
          ads_enabled: !!r.data?.ads_enabled,
          frequency: r.data?.frequency || 3,
          tier: r.data?.tier || "free",
        };
      }).catch(() => { /* network blip — keep defaults */ });
    load();
    // Refresh on premium upgrade/downgrade (event fired by AuthContext)
    window.addEventListener("o1:premium-changed", load);
    return () => {
      cancelled = true;
      window.removeEventListener("o1:premium-changed", load);
    };
  }, []);

  const current = index >= 0 ? queue[index] : null;

  // ----- HTML5 Audio init -----
  useEffect(() => {
    const a = new Audio();
    a.preload = "auto";
    a.volume = volume;
    audioRef.current = a;
    const onTime = () => {
      if (engineRef.current === "audio") setProgress(a.currentTime * 1000);
    };
    const onMeta = () => {
      if (engineRef.current === "audio") setDuration((a.duration || 0) * 1000);
    };
    const onEnd = () => {
      if (engineRef.current === "audio") handleEndedRef.current?.();
    };
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("ended", onEnd);
    return () => {
      a.pause();
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("ended", onEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----- YouTube IFrame init (hidden) -----
  useEffect(() => {
    let cancelled = false;
    loadYouTubeAPI().then((YT) => {
      if (cancelled) return;
      // Ensure the host div exists
      let host = document.getElementById("o1-yt-host");
      if (!host) {
        host = document.createElement("div");
        host.id = "o1-yt-host";
        host.style.cssText =
          "position:fixed;left:-9999px;top:-9999px;width:1px;height:1px;opacity:0;pointer-events:none;";
        document.body.appendChild(host);
      }
      ytPlayerRef.current = new YT.Player(host, {
        height: "1",
        width: "1",
        playerVars: { autoplay: 0, controls: 0, disablekb: 1, playsinline: 1, modestbranding: 1, rel: 0 },
        events: {
          onReady: (e) => {
            ytReadyRef.current = true;
            try { e.target.setVolume(Math.round(volume * 100)); } catch { /* noop */ }
          },
          onStateChange: (e) => {
            if (engineRef.current !== "youtube") return;
            const PS = window.YT?.PlayerState || {};
            if (e.data === PS.PLAYING) {
              setPlaying(true);
              const dur = ytPlayerRef.current?.getDuration?.() || 0;
              if (dur) setDuration(dur * 1000);
              startYTPolling();
            } else if (e.data === PS.PAUSED || e.data === PS.BUFFERING) {
              if (e.data === PS.PAUSED) setPlaying(false);
            } else if (e.data === PS.ENDED) {
              stopYTPolling();
              setPlaying(false);
              handleEndedRef.current?.();
            }
          },
        },
      });
    });
    return () => {
      cancelled = true;
      stopYTPolling();
      try { ytPlayerRef.current?.destroy?.(); } catch { /* noop */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startYTPolling = () => {
    stopYTPolling();
    ytPollRef.current = setInterval(() => {
      const p = ytPlayerRef.current;
      if (!p || engineRef.current !== "youtube") return;
      try {
        setProgress((p.getCurrentTime?.() || 0) * 1000);
        const d = p.getDuration?.() || 0;
        if (d) setDuration(d * 1000);
      } catch { /* noop */ }
    }, 500);
  };
  const stopYTPolling = () => {
    if (ytPollRef.current) {
      clearInterval(ytPollRef.current);
      ytPollRef.current = null;
    }
  };

  // sync volume to both engines
  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
    try {
      if (ytReadyRef.current) ytPlayerRef.current?.setVolume?.(Math.round(volume * 100));
    } catch { /* noop */ }
  }, [volume]);

  const loadLikes = useCallback(async () => {
    if (!user) {
      setLikedIds(new Set());
      return;
    }
    try {
      const r = await api.get("/library/likes/ids");
      setLikedIds(new Set(r.data));
    } catch {
      /* ignore */
    }
  }, [user]);

  useEffect(() => { loadLikes(); }, [loadLikes]);

  // ----- Saved Albums -----
  const [savedAlbumIds, setSavedAlbumIds] = useState(new Set());
  const loadSavedAlbums = useCallback(async () => {
    if (!user) {
      setSavedAlbumIds(new Set());
      return;
    }
    try {
      const r = await api.get("/library/albums/ids");
      setSavedAlbumIds(new Set((r.data || []).map(String)));
    } catch {
      /* ignore */
    }
  }, [user]);
  useEffect(() => { loadSavedAlbums(); }, [loadSavedAlbums]);

  const isAlbumSaved = useCallback(
    (id) => savedAlbumIds.has(String(id)),
    [savedAlbumIds],
  );

  const toggleSavedAlbum = useCallback(async (album) => {
    if (!user) {
      toast.error("Sign in to save albums");
      return;
    }
    if (!album?.collectionId) return;
    const aid = String(album.collectionId);
    // Optimistic toggle
    setSavedAlbumIds((prev) => {
      const next = new Set(prev);
      if (next.has(aid)) next.delete(aid);
      else next.add(aid);
      return next;
    });
    try {
      const r = await api.post("/library/albums", {
        collectionId: aid,
        collectionName: album.collectionName,
        artistName: album.artistName,
        artistId: album.artistId,
        artworkUrl100: album.artworkUrl100,
        primaryGenreName: album.primaryGenreName,
        releaseDate: album.releaseDate,
        trackCount: album.trackCount,
      });
      toast.success(r.data?.saved ? "Album saved" : "Album removed");
    } catch (e) {
      // Roll back on error
      setSavedAlbumIds((prev) => {
        const next = new Set(prev);
        if (next.has(aid)) next.delete(aid);
        else next.add(aid);
        return next;
      });
      toast.error("Couldn't update album");
    }
  }, [user]);

  const recordPlay = useCallback(async (track) => {
    if (!user || !track) return;
    try { await api.post("/library/recent", track); } catch { /* ignore */ }
  }, [user]);

  // Stop whichever engine is currently active
  const stopAll = useCallback(() => {
    try { audioRef.current?.pause(); } catch { /* noop */ }
    try { if (ytReadyRef.current) ytPlayerRef.current?.stopVideo?.(); } catch { /* noop */ }
    stopYTPolling();
  }, []);

  // Forward-reference ref to ``_advanceTrack`` (defined below). Lets
  // ``_playYouTubeForTrack`` ask the queue to auto-advance when a paid
  // user lands on a track YouTube can't resolve — without forcing them
  // back to the 30s preview.
  const advanceTrackRef = useRef(() => {});

  // Anti-infinite-loop guard for paid-user full-track auto-skip.
  // If a paid user's queue is entirely indie tracks that neither YouTube
  // nor Audius can resolve, we'd ping-pong through every track and end up
  // playing nothing. Track consecutive misses and bail out gracefully
  // after N skips (showing a toast).
  const consecutiveFullTrackMissesRef = useRef(0);
  const MAX_CONSECUTIVE_MISSES = 6;

  // ----- Core: load and play a track on the right engine -----
  const _playYouTubeForTrack = useCallback(async (track) => {
    // Full-track playback for ALL users. We always try YouTube first so the
    // listener gets the complete song; the 30s iTunes preview is the
    // last-resort fallback — and for paid users it's disabled entirely
    // (they auto-skip to the next playable track instead).
    if (!ytReadyRef.current) {
      // Wait briefly for the IFrame API to come up
      await new Promise((res) => setTimeout(res, 800));
    }
    const q = `${track.trackName || ""} ${track.artistName || ""}`.trim();
    let videoId = track._youtubeId || null;
    if (!videoId) {
      try {
        // Always request the relaxed-match pass for higher catalog
        // coverage. The backend's strict pass still runs first, so we
        // don't lose accuracy — we just gain a fallback that rescues
        // niche tracks whose title subtitles or accents differ from
        // iTunes' canonical form.
        const r = await api.get(`/youtube/resolve`, {
          params: { q, title: track.trackName, artist: track.artistName, tier: "premium" },
        });
        videoId = r.data?.videoId || null;
        track._youtubeId = videoId; // memoize on the track object
      } catch {
        videoId = null;
      }
    }
    if (!videoId || !ytPlayerRef.current) {
      // YouTube didn't have this track. Try Audius as a last-resort
      // full-track lookup — most up-and-coming indie artists publish
      // their full audio there even when YouTube doesn't index them.
      // We try Audius for EVERYONE here (not just premium) because a
      // successful Audius hit is just as good as YouTube and costs us
      // nothing extra.
      if (audioRef.current) {
        try {
          const ar = await api.get("/audius/resolve-track", {
            params: { title: track.trackName || "", artist: track.artistName || "" },
          });
          const streamUrl = ar.data?.streamUrl;
          if (streamUrl) {
            engineRef.current = "audio";
            audioRef.current.src = streamUrl;
            audioRef.current.play().catch(() => setPlaying(false));
            setPlaying(true);
            consecutiveFullTrackMissesRef.current = 0;
            return;
          }
        } catch { /* network blip — fall through */ }
      }

      // No full track available anywhere. Behaviour now depends on tier:
      //
      //   • FREE / no-account: play the 30s iTunes/Deezer preview so the
      //     queue stays alive. Free users explicitly trade full-track
      //     access for being on the free tier.
      //   • PAID (premium/trial/artist): auto-advance to the next track
      //     in the queue. Paid subscribers pay for full-track listening;
      //     we will NEVER drop them onto a 30s preview. To avoid a
      //     queue-of-indie-tracks softlock, we bail with a toast after
      //     MAX_CONSECUTIVE_MISSES consecutive skips.
      if (isPremiumRef.current) {
        consecutiveFullTrackMissesRef.current += 1;
        if (consecutiveFullTrackMissesRef.current > MAX_CONSECUTIVE_MISSES) {
          // Queue is dry — stop and let the user pick something else.
          consecutiveFullTrackMissesRef.current = 0;
          setPlaying(false);
          toast.message("No full tracks available for this run.", {
            description: "These up-and-coming artists aren't on YouTube or Audius yet. Try another playlist or upload their music to 1o1sound.",
          });
          return;
        }
        // Toast once to explain what's happening (only on the FIRST skip
        // of a streak, so we don't spam).
        if (consecutiveFullTrackMissesRef.current === 1) {
          toast.message("Skipping — full track not found", {
            description: `"${track.trackName}" isn't on YouTube or Audius yet. Premium auto-skipped to the next track.`,
          });
        }
        // Auto-advance to the next track. ``advanceTrackRef`` is set by
        // the effect below to point at ``_advanceTrack``.
        try { advanceTrackRef.current?.(); } catch { /* noop */ }
        return;
      }

      // Free tier: 30s preview is the last-resort fallback.
      if (track.previewUrl && audioRef.current) {
        engineRef.current = "audio";
        audioRef.current.src = track.previewUrl;
        audioRef.current.play().catch(() => setPlaying(false));
        setPlaying(true);
      } else {
        setPlaying(false);
      }
      return;
    }
    engineRef.current = "youtube";
    setProgress(0);
    setDuration(0);
    try {
      ytPlayerRef.current.loadVideoById(videoId);
      ytPlayerRef.current.playVideo();
      setPlaying(true);
      // YouTube succeeded — reset the miss streak so a single later skip
      // doesn't immediately abort the queue.
      consecutiveFullTrackMissesRef.current = 0;
    } catch {
      setPlaying(false);
    }
  }, []);

  // ----- Audio commercial helper: speaks an ad via SpeechSynthesis -----
  // Returns a Promise that resolves when the ad finishes (or errors out).
  // For premium tiers / when ads are disabled, resolves immediately.
  const _playAdIfDue = useCallback(async () => {
    const cfg = adsConfigRef.current;
    if (!cfg.ads_enabled) return false;
    // Don't show an ad on the very first play of the session — only every
    // Nth play after that.
    playCountRef.current += 1;
    const n = Math.max(1, cfg.frequency || 3);
    if (playCountRef.current === 1 || playCountRef.current % n !== 1) {
      return false;
    }
    try {
      const r = await api.get("/ads/next", { params: { tracks_played: playCountRef.current } });
      if (r.data?.skip || !r.data?.ad) return false;
      const ad = r.data.ad;
      // Pause whatever's currently playing
      try { audioRef.current?.pause(); } catch { /* noop */ }
      try { if (ytReadyRef.current) ytPlayerRef.current?.pauseVideo?.(); } catch { /* noop */ }
      stopYTPolling();
      setPlaying(false);
      setCurrentAd({ ad });
      adImpressionRef.current = {
        id: ad.id,
        campaign: ad.campaign,
        sig: ad._sig,
        started_at: Date.now(),
      };

      // Speak via the browser's SpeechSynthesis API. Resolves on end/error.
      await new Promise((resolve) => {
        try {
          window.speechSynthesis?.cancel();
        } catch { /* noop */ }
        if (!window.speechSynthesis || !window.SpeechSynthesisUtterance) {
          // No TTS support — just hold a 4-second placeholder so the user
          // visually sees the ad banner, then continue.
          setTimeout(resolve, 4000);
          return;
        }
        const u = new window.SpeechSynthesisUtterance(ad.message);
        u.rate = 1.05;
        u.pitch = 1.0;
        u.volume = 1.0;
        // Best-effort voice preference (browsers expose voices async).
        try {
          const voices = window.speechSynthesis.getVoices() || [];
          if (voices.length && ad.voice_pref) {
            const want = String(ad.voice_pref).toLowerCase();
            const match =
              voices.find((v) => v.name.toLowerCase().includes(want)) ||
              voices.find((v) => (v.gender || "").toLowerCase() === want);
            if (match) u.voice = match;
          }
        } catch { /* noop */ }
        u.onend = () => resolve();
        u.onerror = () => resolve();
        window.speechSynthesis.speak(u);
      });

      // Log impression (fire-and-forget)
      const imp = adImpressionRef.current;
      if (imp) {
        api.post("/ads/impression", {
          ad_id: imp.id,
          campaign: imp.campaign,
          _sig: imp.sig,
          listened_ms: Date.now() - imp.started_at,
          skipped: false,
        }).catch(() => { /* noop */ });
      }
      setCurrentAd(null);
      adImpressionRef.current = null;
      return true;
    } catch {
      setCurrentAd(null);
      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Skip the currently playing ad — premium-only. Free users can't skip
  // (that's the whole point); for them the button routes to /premium.
  const skipAd = useCallback(({ ctaClicked = false } = {}) => {
    try { window.speechSynthesis?.cancel(); } catch { /* noop */ }
    const imp = adImpressionRef.current;
    if (imp) {
      api.post("/ads/impression", {
        ad_id: imp.id,
        campaign: imp.campaign,
        _sig: imp.sig,
        listened_ms: Date.now() - imp.started_at,
        skipped: true,
        cta_clicked: ctaClicked,
      }).catch(() => { /* noop */ });
    }
    setCurrentAd(null);
    adImpressionRef.current = null;
  }, []);

  const playAt = useCallback(async (i, q) => {
    const list = q || queue;
    const track = list[i];
    if (!track) return;
    setQueue(list);
    setIndex(i);
    stopAll();

    // Free-tier ad insertion (premium tiers no-op immediately)
    await _playAdIfDue();

    if (isFullTrack(track)) {
      // Audius / direct full MP3 → HTML5 audio
      if (!track.previewUrl) return;
      engineRef.current = "audio";
      setProgress(0);
      setDuration(0);
      if (audioRef.current) {
        audioRef.current.src = track.previewUrl;
        audioRef.current.play().catch(() => setPlaying(false));
        setPlaying(true);
      }
    } else {
      // iTunes 30s preview → upgrade to YouTube full track
      _playYouTubeForTrack(track);
    }
    recordPlay(track);
  }, [queue, recordPlay, stopAll, _playYouTubeForTrack, _playAdIfDue]);

  const playTracks = useCallback((tracks, startIndex = 0) => {
    // Allow tracks without previewUrl now — YouTube fallback can resolve them
    const valid = (tracks || []).filter(
      (t) => t && (t.previewUrl || t.trackName)
    );
    if (valid.length === 0) return;
    const startTrack = tracks[startIndex];
    const newStart = valid.findIndex((t) => t.trackId === startTrack?.trackId);
    playAt(newStart >= 0 ? newStart : 0, valid);
  }, [playAt]);

  // ----- Queue management -----
  const addToQueue = useCallback((track, position = "end") => {
    if (!track) return;
    setQueue((prev) => {
      // de-dupe: don't add the same trackId twice in a row
      const next = [...prev];
      if (position === "next") {
        const insertAt = Math.max(index + 1, 0);
        next.splice(insertAt, 0, track);
      } else {
        next.push(track);
      }
      return next;
    });
  }, [index]);

  const removeFromQueue = useCallback((removeIndex) => {
    setQueue((prev) => {
      if (removeIndex < 0 || removeIndex >= prev.length) return prev;
      const next = prev.filter((_, i) => i !== removeIndex);
      // Adjust currently-playing index if we removed something before it
      if (removeIndex < index) {
        setIndex((p) => p - 1);
      } else if (removeIndex === index) {
        // Removed the currently playing track → stop playback
        stopAll();
        setPlaying(false);
        setIndex(-1);
        setProgress(0);
        setDuration(0);
      }
      return next;
    });
  }, [index, stopAll]);

  const reorderQueue = useCallback((from, to) => {
    if (from === to) return;
    setQueue((prev) => {
      if (from < 0 || from >= prev.length || to < 0 || to >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      // Recompute the active index so playback stays on the same track
      setIndex((cur) => {
        if (cur === from) return to;
        if (from < cur && to >= cur) return cur - 1;
        if (from > cur && to <= cur) return cur + 1;
        return cur;
      });
      return next;
    });
  }, []);

  const clearQueue = useCallback(() => {
    stopAll();
    setQueue([]);
    setIndex(-1);
    setPlaying(false);
    setProgress(0);
    setDuration(0);
  }, [stopAll]);

  const togglePlay = useCallback(() => {
    if (!current) return;
    if (engineRef.current === "youtube") {
      if (!ytReadyRef.current) return;
      try {
        if (playing) {
          ytPlayerRef.current.pauseVideo();
          setPlaying(false);
        } else {
          ytPlayerRef.current.playVideo();
          setPlaying(true);
        }
      } catch { /* noop */ }
      return;
    }
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      audioRef.current.play().catch(() => {});
      setPlaying(true);
    }
  }, [playing, current]);

  // Core advance helper — no paywall check. Used by auto-end-of-track
  // and (after the skip-limit check) by the public ``next``.
  const _advanceTrack = useCallback(() => {
    if (queue.length === 0) return;
    let n = index + 1;
    if (shuffle) n = Math.floor(Math.random() * queue.length);
    if (n >= queue.length) {
      if (repeat === "all") n = 0;
      else {
        setPlaying(false);
        stopAll();
        return;
      }
    }
    playAt(n, queue);
  }, [index, queue, shuffle, repeat, playAt, stopAll]);

  // Keep the forward-reference ref in sync so ``_playYouTubeForTrack``
  // (defined earlier, before _advanceTrack exists in scope) can auto-skip
  // paid users past tracks YouTube failed to resolve.
  useEffect(() => {
    advanceTrackRef.current = _advanceTrack;
  }, [_advanceTrack]);

  // Public ``next`` — gated by the skip-limit paywall for free users.
  // Premium / Artist / Trial bypass the API call entirely. Auto-advance
  // at end-of-track uses ``_advanceTrack`` directly, so natural
  // playback continues uncounted.
  const isPremiumActive = !!premium?.active;
  const next = useCallback(async () => {
    if (queue.length === 0) return;
    if (isPremiumActive) {
      _advanceTrack();
      return;
    }
    try {
      const r = await api.post("/paywall/skip-attempt");
      if (r.data?.allowed === false) {
        showPaywall("skip", { reset_at: r.data?.reset_at });
        return;
      }
    } catch {
      // Network blip — fail open so a transient error never blocks
      // playback. Worst case the user gets one extra skip.
    }
    _advanceTrack();
  }, [queue, isPremiumActive, _advanceTrack, showPaywall]);

  const prev = useCallback(() => {
    if (queue.length === 0) return;
    if (engineRef.current === "audio" && audioRef.current && audioRef.current.currentTime > 3) {
      audioRef.current.currentTime = 0;
      return;
    }
    if (engineRef.current === "youtube" && ytReadyRef.current) {
      try {
        const t = ytPlayerRef.current.getCurrentTime?.() || 0;
        if (t > 3) {
          ytPlayerRef.current.seekTo(0, true);
          return;
        }
      } catch { /* noop */ }
    }
    let p = index - 1;
    if (p < 0) p = repeat === "all" ? queue.length - 1 : 0;
    playAt(p, queue);
  }, [index, queue, repeat, playAt]);

  // Keep handleEnded fresh via a ref to avoid stale closures inside engine callbacks
  const handleEndedRef = useRef(() => {});
  useEffect(() => {
    handleEndedRef.current = () => {
      if (repeat === "one") {
        if (engineRef.current === "audio" && audioRef.current) {
          audioRef.current.currentTime = 0;
          audioRef.current.play().catch(() => {});
        } else if (engineRef.current === "youtube" && ytReadyRef.current) {
          try {
            ytPlayerRef.current.seekTo(0, true);
            ytPlayerRef.current.playVideo();
          } catch { /* noop */ }
        }
        return;
      }
      // Auto-advance at end-of-track — bypass paywall (not a user skip).
      _advanceTrack();
    };
  }, [_advanceTrack, repeat]);

  const seek = useCallback((ms) => {
    if (engineRef.current === "youtube") {
      if (!ytReadyRef.current) return;
      try { ytPlayerRef.current.seekTo(ms / 1000, true); } catch { /* noop */ }
      setProgress(ms);
      return;
    }
    if (!audioRef.current) return;
    audioRef.current.currentTime = ms / 1000;
    setProgress(ms);
  }, []);

  const toggleLike = useCallback(async (track) => {
    if (!user) return { needsAuth: true };
    try {
      const r = await api.post("/library/likes", track);
      setLikedIds((prevSet) => {
        const s = new Set(prevSet);
        if (r.data.liked) s.add(track.trackId);
        else s.delete(track.trackId);
        return s;
      });
      return r.data;
    } catch {
      return null;
    }
  }, [user]);

  const isLiked = useCallback((id) => likedIds.has(id), [likedIds]);

  // ====================================================================
  // MediaSession API — system-level "Now Playing" + lock-screen controls
  // ====================================================================
  // - macOS Control Center / Touch Bar
  // - Windows Media Control / SMTC
  // - Android lock screen + notification shade
  // - iOS lock screen + Control Center (Safari, after user gesture)
  // - Chrome media notification on tab background
  //
  // Wires media-key / Bluetooth headset play/pause/prev/next to our engine,
  // keeps the OS UI metadata + position in sync with whatever's playing
  // on either the HTML5 audio OR YouTube IFrame engine.
  // ====================================================================
  const upgradeArtwork = (url) => {
    if (!url) return null;
    // iTunes art ships as 100x100bb.jpg by default — bump to 600x600 for
    // a sharp lock-screen image. Deezer & uploads are already large.
    return url
      .replace(/\/100x100bb\.(jpg|png|webp)/i, "/600x600bb.$1")
      .replace(/\/30x30bb\.(jpg|png|webp)/i, "/600x600bb.$1");
  };

  // Set metadata + position state whenever the current track or progress changes
  useEffect(() => {
    if (typeof navigator === "undefined" || !("mediaSession" in navigator)) return;
    if (!current) {
      try { navigator.mediaSession.metadata = null; } catch { /* noop */ }
      return;
    }
    try {
      const art = upgradeArtwork(current.artworkUrl100);
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title: current.trackName || "Unknown track",
        artist: current.artistName || "Unknown artist",
        album: current.collectionName || "1o1sound",
        artwork: art
          ? [
              { src: art, sizes: "96x96",   type: "image/jpeg" },
              { src: art, sizes: "128x128", type: "image/jpeg" },
              { src: art, sizes: "192x192", type: "image/jpeg" },
              { src: art, sizes: "256x256", type: "image/jpeg" },
              { src: art, sizes: "384x384", type: "image/jpeg" },
              { src: art, sizes: "512x512", type: "image/jpeg" },
            ]
          : [],
      });
    } catch (e) { /* metadata constructor not supported — noop */ }
  }, [current]);

  // Keep playbackState + positionState in sync so the OS scrubber tracks us.
  useEffect(() => {
    if (typeof navigator === "undefined" || !("mediaSession" in navigator)) return;
    try {
      navigator.mediaSession.playbackState = playing ? "playing" : (current ? "paused" : "none");
    } catch { /* noop */ }
  }, [playing, current]);

  // Periodically push positionState (every ~1s) — required for the OS scrubber.
  useEffect(() => {
    if (typeof navigator === "undefined" || !("mediaSession" in navigator)) return;
    if (!current || !duration) return;
    try {
      if (navigator.mediaSession.setPositionState) {
        navigator.mediaSession.setPositionState({
          duration: Math.max(0, duration / 1000),
          playbackRate: 1.0,
          position: Math.min(Math.max(0, progress / 1000), duration / 1000),
        });
      }
    } catch { /* noop — happens if duration < position briefly */ }
  }, [progress, duration, current]);

  // Wire system media keys / lock-screen buttons to our engine.
  // Refs are used inside handlers because the MediaSession action callbacks
  // capture their closure at install time — we'd otherwise see stale state.
  const togglePlayRef = useRef(togglePlay);
  const nextRef = useRef(next);
  const prevRef = useRef(prev);
  const seekRef = useRef(seek);
  useEffect(() => { togglePlayRef.current = togglePlay; }, [togglePlay]);
  useEffect(() => { nextRef.current = next; }, [next]);
  useEffect(() => { prevRef.current = prev; }, [prev]);
  useEffect(() => { seekRef.current = seek; }, [seek]);

  useEffect(() => {
    if (typeof navigator === "undefined" || !("mediaSession" in navigator)) return;
    const set = (action, fn) => {
      try { navigator.mediaSession.setActionHandler(action, fn); } catch { /* unsupported action */ }
    };
    set("play",          () => { if (!playing) togglePlayRef.current?.(); });
    set("pause",         () => { if (playing)  togglePlayRef.current?.(); });
    set("previoustrack", () => prevRef.current?.());
    set("nexttrack",     () => nextRef.current?.());
    set("stop",          () => { stopAll(); setPlaying(false); });
    set("seekbackward",  (d) => {
      const off = (d?.seekOffset || 10) * 1000;
      seekRef.current?.(Math.max(0, progress - off));
    });
    set("seekforward",   (d) => {
      const off = (d?.seekOffset || 10) * 1000;
      seekRef.current?.(Math.min(duration || 0, progress + off));
    });
    set("seekto",        (d) => {
      if (typeof d?.seekTime !== "number") return;
      seekRef.current?.(d.seekTime * 1000);
    });
    return () => {
      ["play","pause","previoustrack","nexttrack","stop","seekbackward","seekforward","seekto"].forEach((a) => set(a, null));
    };
  }, [playing, progress, duration, stopAll]);

  // ----- iOS background-audio nudge -----
  // iOS Safari aggressively pauses backgrounded audio unless we periodically
  // assert playback. When the page hides, mark the audio element as "still
  // playing" via a silent currentTime touch so the OS keeps the session alive.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const onVis = () => {
      // Only intervene for the HTML5 audio engine (YouTube has its own keep-alive)
      if (engineRef.current !== "audio" || !audioRef.current) return;
      if (document.visibilityState === "hidden" && playing) {
        // Nudge: re-issue play() so the user-gesture token persists in iOS Safari
        audioRef.current.play().catch(() => { /* iOS may reject; OS controls will resume */ });
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [playing]);

  return (
    <PlayerContext.Provider
      value={{
        queue, index, current, playing, progress, duration, volume, shuffle, repeat,
        playTracks, playAt, togglePlay, next, prev, seek, setVolume, setShuffle,
        setRepeat, toggleLike, isLiked, loadLikes,
        addToQueue, removeFromQueue, reorderQueue, clearQueue,
        currentAd, skipAd,
      }}
    >
      {children}
    </PlayerContext.Provider>
  );
};

export const usePlayer = () => useContext(PlayerContext);
